import { memo } from "react"
import { FiSearch } from "react-icons/fi"

import { AttendanceActionsMenu } from "@/components/layout/Common/AttendanceActionsMenu"
import type { AttendanceResponse } from "@/types/attendance"
import { EmptyState, Flex, Table, VStack } from "@chakra-ui/react"

const formatFieldValue = (value: unknown, type: string): string => {
  if (value === null || value === undefined || value === "") {
    return "N/A"
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "N/A"
  }

  if (type === "bool") {
    if (typeof value === "string") {
      if (value.toLowerCase() === "true") return "Yes"
      if (value.toLowerCase() === "false") return "No"
      return value
    }
    return value ? "Yes" : "No"
  }

  if (type === "datetime" || type === "date" || type === "time") {
    const dateValue = new Date(String(value))
    if (!Number.isNaN(dateValue.getTime())) {
      if (type === "date") return dateValue.toLocaleDateString()
      if (type === "time") return dateValue.toLocaleTimeString()
      return dateValue.toLocaleString()
    }
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value)
    } catch (error) {
      return "N/A"
    }
  }

  return String(value)
}

interface AttendanceListProps {
  attendances: AttendanceResponse[]
}

const AttendanceList = memo(({ attendances }: AttendanceListProps) => {
  if (!attendances?.length) {
    return (
      <EmptyState.Root>
        <EmptyState.Content>
          <EmptyState.Indicator>
            <FiSearch />
          </EmptyState.Indicator>
          <VStack textAlign="center">
            <EmptyState.Title>No attendances yet</EmptyState.Title>
            <EmptyState.Description>
              Create a new attendance to see it listed here.
            </EmptyState.Description>
          </VStack>
        </EmptyState.Content>
      </EmptyState.Root>
    )
  }

  return (
    <Table.Root size={{ base: "sm", md: "md" }}>
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeader w="sm">ID</Table.ColumnHeader>
          <Table.ColumnHeader>Enrollment Id</Table.ColumnHeader>
          <Table.ColumnHeader>Class Session Id</Table.ColumnHeader>
          <Table.ColumnHeader>Student Id</Table.ColumnHeader>
          <Table.ColumnHeader>Class Date</Table.ColumnHeader>
          <Table.ColumnHeader>Class Time</Table.ColumnHeader>
          <Table.ColumnHeader>Status</Table.ColumnHeader>
          <Table.ColumnHeader>Check In Time</Table.ColumnHeader>
          <Table.ColumnHeader>Check Out Time</Table.ColumnHeader>
          <Table.ColumnHeader>Is Makeup Class</Table.ColumnHeader>
          <Table.ColumnHeader>Makeup For Date</Table.ColumnHeader>
          <Table.ColumnHeader>Instructor Notes</Table.ColumnHeader>
          <Table.ColumnHeader>Skills Practiced</Table.ColumnHeader>
          <Table.ColumnHeader>Behavior Notes</Table.ColumnHeader>
          <Table.ColumnHeader>Parent Notification Sent</Table.ColumnHeader>
          <Table.ColumnHeader>Marked By User Id</Table.ColumnHeader>
          <Table.ColumnHeader>Notes</Table.ColumnHeader>
          <Table.ColumnHeader>Created By</Table.ColumnHeader>
          <Table.ColumnHeader>Created</Table.ColumnHeader>
          <Table.ColumnHeader>Updated By</Table.ColumnHeader>
          <Table.ColumnHeader>Updated</Table.ColumnHeader>
          <Table.ColumnHeader w="xs">Actions</Table.ColumnHeader>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {attendances.map((attendance) => (
          <Table.Row key={attendance.id}>
            <Table.Cell truncate maxW="sm">
              {attendance.id}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(attendance.enrollment_id, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(attendance.class_session_id, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(attendance.student_id, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(attendance.class_date, "date")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(attendance.class_time, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(attendance.status, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(attendance.check_in_time, "datetime")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(attendance.check_out_time, "datetime")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(attendance.is_makeup_class, "bool")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(attendance.makeup_for_date, "date")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(attendance.instructor_notes, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(attendance.skills_practiced, "dict")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(attendance.behavior_notes, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(attendance.parent_notification_sent, "bool")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(attendance.marked_by_user_id, "str")}
            </Table.Cell>
            <Table.Cell>{formatFieldValue(attendance.notes, "str")}</Table.Cell>
            <Table.Cell>
              {formatFieldValue(attendance.created_by, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(attendance.created_at, "datetime")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(attendance.updated_by, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(attendance.updated_at, "datetime")}
            </Table.Cell>
            <Table.Cell>
              <Flex justify="flex-end">
                <AttendanceActionsMenu attendance={attendance} />
              </Flex>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  )
})

AttendanceList.displayName = "AttendanceList"

export default AttendanceList
